import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, PencilSimple, Trash, X, ListChecks, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import ModalPortal from "@/components/ModalPortal";
import MediaGalleryModal from "@/components/MediaGalleryModal";
import GlobalTaskLogSearch from "@/components/GlobalTaskLogSearch";

const COLORS = ["#C25934", "#2E7D32", "#1A2E25", "#F57C00", "#5C4033", "#264235", "#8B5A3C"];

const empty = { name: "", description: "", color: COLORS[0], active: true };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [mediaFor, setMediaFor] = useState(null);
  const [fileCounts, setFileCounts] = useState({}); // { task_id: count }

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/tasks");
      setTasks(r.data);
      // Aggregerad räknare — 1 anrop istället för N
      try {
        const cr = await api.get("/tasks/file-counts");
        setFileCounts(cr.data || {});
      } catch {
        setFileCounts({});
      }
    } catch {
      toast.error("Kunde inte hämta moment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("sv-SE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      description: t.description || "",
      color: t.color || COLORS[0],
      active: t.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Namn krävs"); return; }
    try {
      if (editing) {
        await api.patch(`/tasks/${editing.task_id}`, form);
        toast.success("Moment uppdaterat");
      } else {
        await api.post("/tasks", form);
        toast.success("Moment skapat");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort momentet?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Borttaget");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Moment</h1>
          <p className="text-text-muted mt-1">{tasks.length} moment definierade</p>
        </div>
        <button
          data-testid="new-task-button"
          onClick={openCreate}
          className="tk-btn-primary inline-flex items-center gap-2"
        >
          <Plus size={18} weight="bold" /> Nytt moment
        </button>
      </header>

      {/* Global sökning över alla momentloggar */}
      <GlobalTaskLogSearch />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-text-muted text-sm col-span-full">Laddar…</p>
        ) : tasks.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              testId="tasks-empty"
              icon={ListChecks}
              title="Inga moment ännu"
              description="Moment är de arbetsuppgifter dina anställda kan stämpla tid på (t.ex. Planering, Utförande, Kontroll). Skapa några moment så de går att koppla till objekt."
              action={
                <button
                  data-testid="empty-new-task-button"
                  onClick={openCreate}
                  className="tk-btn-primary inline-flex items-center gap-2"
                >
                  <Plus size={18} weight="bold" /> Skapa första momentet
                </button>
              }
            />
          </div>
        ) : (
          tasks.map((t) => (
            <div
              key={t.task_id}
              data-testid={`task-card-${t.task_id}`}
              className="tk-card p-5 flex items-start gap-3"
            >
              <div
                className="w-3 h-12 rounded-full flex-shrink-0 mt-1"
                style={{ background: t.color || COLORS[0] }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg truncate">{t.name}</h3>
                  {!t.active && (
                    <span className="tk-pill text-[10px]">Inaktiv</span>
                  )}
                </div>
                {t.description && (
                  <p className="text-xs text-text-muted mt-1 line-clamp-2">{t.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`media-task-${t.task_id}`}
                  onClick={() => setMediaFor(t)}
                  className="text-text-muted hover:text-brand p-1 relative"
                  title="Foto, film & bilagor"
                >
                  <Paperclip size={16} />
                  {fileCounts[t.task_id] > 0 && (
                    <span className="absolute -top-1 -right-1 bg-brand text-white text-[9px] font-medium rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                      {fileCounts[t.task_id]}
                    </span>
                  )}
                </button>
                <button
                  data-testid={`edit-task-${t.task_id}`}
                  onClick={() => openEdit(t)}
                  className="text-text-muted hover:text-text-primary p-1"
                  title="Redigera"
                >
                  <PencilSimple size={16} />
                </button>
                <button
                  data-testid={`delete-task-${t.task_id}`}
                  onClick={() => remove(t.task_id)}
                  className="text-text-muted hover:text-[var(--status-danger)] p-1"
                  title="Ta bort"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {open && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-surface rounded-2xl max-w-md w-full p-6 border border-app"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h2 className="font-heading text-2xl">{editing ? "Redigera moment" : "Nytt moment"}</h2>
              <button onClick={() => setOpen(false)} className="text-text-muted">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Namn *</div>
                <input
                  data-testid="task-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="tk-input"
                  placeholder="t.ex. Montering"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Beskrivning</div>
                <textarea
                  data-testid="task-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="tk-input"
                  rows={2}
                />
              </label>
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Färg</div>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      data-testid={`color-swatch-${c}`}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-9 h-9 rounded-full border-2 transition-transform ${
                        form.color === c ? "scale-110 border-text-primary" : "border-transparent"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="task-active-checkbox"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Aktivt moment (visas i stämpelvyn)
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setOpen(false)} className="tk-btn-outline">Avbryt</button>
              <button data-testid="save-task-button" onClick={save} className="tk-btn-primary">
                {editing ? "Spara" : "Skapa"}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {mediaFor && (
        <MediaGalleryModal
          basePath={`/tasks/${mediaFor.task_id}`}
          title={`Bilagor — ${mediaFor.name}`}
          onClose={() => setMediaFor(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}
